import type { GetStaticProps } from 'next'
import { NextSeo } from 'next-seo'

import { NewSurveyProvider } from '@context/new-survey/useNewSurvey'
import { lang } from '@lib/constants'
import getNewSurvey from '@lib/new-survey/get-new-survey'
import { getCollectionProductsPerPage } from '@lib/operations/collection'
import { getDocumentByUID } from '@lib/prismic/fetch/documents'
import { getGetStartedOffer } from '@lib/prismic/fetch/get-started-offer'
import { getGetStartedPageContent } from '@lib/prismic/fetch/get-started-page'
import type { CollectionProducts } from '@lib/types/collection'
import type { GetStartedOffer } from '@lib/types/get-started-offer'
import type { GetStartedPageContent } from '@lib/types/get-started-page'
import { FALLBACK_PAGE_CONTENT } from '@lib/types/get-started-page'
import fetcher from '@lib/fetcher'

import GetStartedContent from '@components/get-started/GetStartedContent'

type GetStartedPageProps = {
	collectionProducts: CollectionProducts
	offer: GetStartedOffer | null
	pageContent: GetStartedPageContent
}

async function resolveGiftVariantId(handle: string): Promise<string | null> {
	try {
		const { product } = await fetcher({
			query: /* GraphQL */ `
				query getGiftProductVariant($handle: String!) {
					product(handle: $handle) {
						variants(first: 1) {
							nodes {
								id
							}
						}
					}
				}
			`,
			variables: { handle }
		})
		return product?.variants?.nodes?.[0]?.id ?? null
	} catch {
		return null
	}
}

export const getStaticProps: GetStaticProps<GetStartedPageProps> = async ({ previewData }) => {
	if (lang === 'es-es') {
		return { notFound: true }
	}

	let collectionProducts: CollectionProducts = []

	try {
		const page = await getDocumentByUID('subscription_detail', 'monthly-box', {})

		const productsListSlice = (page?.data as any)?.body?.find((slice: any) =>
			slice.slice_type.startsWith('subs_detail_products_list')
		)
		const collectionSlug = productsListSlice?.primary?.products_list_collection_slug

		if (collectionSlug) {
			const collectionResponse = await getCollectionProductsPerPage(collectionSlug, {
				endCursor: null,
				sortBy: 'COLLECTION_DEFAULT',
				reverse: false,
				filters: [],
				onlyFullSizeProducts: false,
				onlyWithStock: false,
				productsPerPage: 250
			})
			collectionProducts = collectionResponse?.products ?? []
		}
	} catch (err) {
		// Non-fatal: landing step renders without product grid if fetch fails
		console.error('[get-started] Failed to fetch landing products:', err)
	}

	let offer: GetStartedOffer | null = await getGetStartedOffer(previewData).catch(() => null)

	if (offer?.type === 'free_gift' && offer.giftProductHandle) {
		const giftVariantId = await resolveGiftVariantId(offer.giftProductHandle)
		if (giftVariantId) {
			offer = { ...offer, giftVariantId }
		}
	}

	const pageContent = await getGetStartedPageContent(previewData).catch(() => FALLBACK_PAGE_CONTENT)

	return {
		props: { collectionProducts, offer, pageContent },
		revalidate: 3600
	}
}

export default function GetStartedPage({ collectionProducts, offer, pageContent }: GetStartedPageProps) {
	const { survey, announcement } = getNewSurvey()

	return (
		<>
			<NextSeo
				title={pageContent.seoTitle}
				description={pageContent.seoDescription}
			/>
			<NewSurveyProvider surveyData={survey}>
				<GetStartedContent
					announcement={announcement}
					collectionProducts={collectionProducts}
					offer={offer}
					pageContent={pageContent}
				/>
			</NewSurveyProvider>
		</>
	)
}
